import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import {
  PageContent,
  Spinner,
  CisContainer,
  Button,
  Row,
  ButtonIcon,
  Modal,
  Table,
} from 'components';
import { connect } from 'react-redux';
import {
  typeOptions,
  deltaClassOptions,
  organizationSizeOptions,
  organizationIndustryOptions,
  agreementStatusOptions,
} from '../../../../shared/Cis/CisOptions';
import {
  loadIpmaCisCreator,
  setIpmaCisPageCreator,
  setIpmaCisPerPageCreator,
  setIpmaCisSortCreator,
  setIpmaCisFiltersCreator,
  removeIpmaCisFiltersCreator,
  setIpmaCisProfileCreator,
} from 'store/actionsCreators';
import PropTypes from 'prop-types';
import { isLoading, globalPropTypes, checkPermissions } from 'helpers';
import {
  loadGlobalCisAgreementsAttributesCreator,
  removeIpmaCisCreator,
} from '../../../../store/actionsCreators';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import CisTable from 'components/Table/CisTable';
import CisPagination from 'components/Table/CisPagination';
import { formatDate } from 'helpers/date';

const Agreements = ({
  loading,
  list,
  loadList,
  setPage,
  setPerPage,
  setSort,
  setFilters,
  setProfile,
  removeAgreement,
  countries,
  memberAssociations,
  levelDomainsLevels,
  levelDomainsDomains,
  loadAgreementsAttributes,
  userPermissions,
}) => {
  const { filters, items, page, pages, perPage, sort, totalItems } = list;
  const { t } = useTranslation();
  const history = useHistory();
  const [portalReady, setPortalReady] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const manageCisPermission = checkPermissions({ roles: ['ipma_cis'] }, userPermissions);

  useEffect(() => {
    if (document.getElementById('ipma-cis-buttons-portal')) {
      setPortalReady(true);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    loadList();
  }, [perPage, sort]);

  useEffect(() => {
    loadAgreementsAttributes();
  }, []);

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    if (direction) {
      setSort(`${key}.${direction}`);
    } else {
      setSort(null);
    }
  };

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadList();
  };

  return (
    <PageContent>
      <CisContainer>
        <CisTable>
          <CisTable.Head>
            <CisTable.Row>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.memberAssociationCountry || ''}
                  onFilter={(val) => handleColumnFilterChange('memberAssociationCountry', val)}
                  filterOptions={countries}
                  filterPlaceholder={t(
                    'page.user.cis.agreements.memberAssociationCountryPlaceholder',
                  )}
                  label={t('page.user.cis.agreements.memberAssociationCountry')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.memberAssociation || ''}
                  onFilter={(val) => handleColumnFilterChange('memberAssociation', val)}
                  filterOptions={memberAssociations}
                  filterPlaceholder={t('page.user.cis.agreements.memberAssociationPlaceholder')}
                  label={t('page.user.cis.agreements.memberAssociation')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.remoteMemberAssociationCountry || ''}
                  onFilter={(val) =>
                    handleColumnFilterChange('remoteMemberAssociationCountry', val)
                  }
                  filterOptions={countries}
                  filterPlaceholder={t(
                    'page.user.cis.agreements.remoteMemberAssociationCountryPlaceholder',
                  )}
                  label={t('page.user.cis.agreements.remoteMemberAssociationCountry')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.remoteMemberAssociation || ''}
                  onFilter={(val) => handleColumnFilterChange('remoteMemberAssociation', val)}
                  filterOptions={memberAssociations}
                  filterPlaceholder={t(
                    'page.user.cis.agreements.remoteMemberAssociationPlaceholder',
                  )}
                  label={t('page.user.cis.agreements.remoteMemberAssociation')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell label={t('page.user.cis.agreements.remoteMemberAssociationId')} />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.level || ''}
                  onFilter={(val) => handleColumnFilterChange('level', val)}
                  filterOptions={levelDomainsLevels}
                  filterPlaceholder={t('page.user.cis.agreements.levelPlaceholder')}
                  label={t('page.user.cis.agreements.level')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.domain || ''}
                  onFilter={(val) => handleColumnFilterChange('domain', val)}
                  filterOptions={levelDomainsDomains}
                  filterPlaceholder={t('page.user.cis.agreements.domainPlaceholder')}
                  label={t('page.user.cis.agreements.domain')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker"
                  filterValue={filters.validFrom || ''}
                  onFilter={(val) => handleColumnFilterChange('validFrom', val)}
                  filterPlaceholder={t('page.user.cis.agreements.validFromPlaceholder')}
                  label={t('page.user.cis.agreements.validFrom')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker"
                  filterValue={filters.validTo || ''}
                  onFilter={(val) => handleColumnFilterChange('validTo', val)}
                  filterPlaceholder={t('page.user.cis.agreements.validToPlaceholder')}
                  label={t('page.user.cis.agreements.validTo')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.status || ''}
                  onFilter={(val) => handleColumnFilterChange('status', val)}
                  filterOptions={agreementStatusOptions}
                  filterPlaceholder={t('page.user.cis.agreements.statusPlaceholder')}
                  label={t('page.user.cis.agreements.status')}
                />
              </CisTable.StickyHeading>
              {manageCisPermission && (
                <CisTable.StickyActionHeading style={{ minWidth: '10rem' }}>
                  <TableHeaderCell
                    label={t('page.user.cis.agreements.actions')}
                    labelAlign="center"
                    isAction={true}
                  />
                </CisTable.StickyActionHeading>
              )}
            </CisTable.Row>
          </CisTable.Head>
          <CisTable.Body>
            {loading ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={11}>
                  <Spinner />
                </CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={11}>{t('global.noResultsFound')}</CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => (
                  <CisTable.Row key={item.id}>
                    <CisTable.Cell>
                      <Table.Label>
                        {t('page.user.cis.agreements.memberAssociationCountry')}:
                      </Table.Label>
                      {item.memberAssociation?.country?.name}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.agreements.memberAssociation')}:</Table.Label>
                      {item.memberAssociation?.name}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>
                        {t('page.user.cis.agreements.remoteMemberAssociationCountry')}:
                      </Table.Label>
                      {item.remoteMemberAssociation?.country?.name}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>
                        {t('page.user.cis.agreements.remoteMemberAssociation')}:
                      </Table.Label>
                      {item.remoteMemberAssociation?.name}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>
                        {t('page.user.cis.agreements.remoteMemberAssociationId')}:
                      </Table.Label>
                      {item.remoteMemberAssociation?.id}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.agreements.level')}:</Table.Label>
                      {item.levelDomain?.level?.name}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.agreements.domain')}:</Table.Label>
                      {item.levelDomain?.domain?.name}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.agreements.validFrom')}:</Table.Label>
                      {item.validFrom && formatDate(item.validFrom)}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.agreements.validTo')}:</Table.Label>
                      {item.validTo && formatDate(item.validTo)}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.agreements.status')}:</Table.Label>
                      {item.status}
                    </CisTable.Cell>
                    {manageCisPermission && (
                      <CisTable.StickyActionCell style={{ minWidth: '9.6rem' }}>
                        <Table.Label>{t('page.user.cis.agreements.actions')}:</Table.Label>
                        <Row style={{ gap: '3px', justifyContent: 'center' }}>
                          <ButtonIcon
                            icon="edit"
                            secondary
                            as={Button}
                            onClick={() => {
                              setProfile(item);
                              history.push(`/ipma/cis/agreements/edit`);
                            }}
                          />
                          <ButtonIcon
                            icon="trash"
                            onClick={() => {
                              setSelectedItem(item);
                              setOpenConfirmModal(true);
                            }}
                          />
                        </Row>
                      </CisTable.StickyActionCell>
                    )}
                  </CisTable.Row>
                ))
              : null}
          </CisTable.Body>
        </CisTable>
      </CisContainer>
      <CisPagination
        styled
        currentPage={page}
        totalPages={pages}
        totalItems={totalItems}
        perPage={perPage}
        onPageChange={(payload) => {
          setPage(payload);
          loadList();
        }}
        onPerPageChange={({ value }) => setPerPage(value)}
      />
      <Modal
        heading={t('page.user.cis.agreements.confirmDelete')}
        isOpen={openConfirmModal && selectedItem?.id}
        confirm={() => {
          removeAgreement(selectedItem.id);
          setOpenConfirmModal(false);
        }}
        cancel={() => setOpenConfirmModal(false)}
        onRequestClose={() => setOpenConfirmModal(false)}
      />
      {portalReady &&
        manageCisPermission &&
        ReactDOM.createPortal(
          <Button style={{ width: '30%' }} secondary as={Link} to={`/ipma/cis/agreements/create`}>
            {t('page.user.cis.agreements.new')}
          </Button>,
          document.getElementById('ipma-cis-buttons-portal'),
        )}
    </PageContent>
  );
};

Agreements.propTypes = {
  loading: PropTypes.bool,
  list: globalPropTypes.ListPropsTypes.isRequired,
  countries: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  memberAssociations: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  levels: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  domains: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  removeFilters: PropTypes.func.isRequired,
  setProfile: PropTypes.func.isRequired,
  removeAgreement: PropTypes.func.isRequired,
};

Agreements.defaultProps = {
  loading: false,
};

const mapStateToProps = ({ loading, ipma, globals, user }) => ({
  loading: isLoading(loading, 'LOAD_IPMA_CIS'),
  list: ipma.cis.agreements,
  countries: globals.countries,
  memberAssociations: globals.availableMA,
  levelDomainsLevels: globals.cis.levelDomainsLevels,
  levelDomainsDomains: globals.cis.levelDomainsDomains,
  userPermissions: user.userPermissions,
});

const mapDispatchToProps = (dispatch) => ({
  loadList: () => dispatch(loadIpmaCisCreator('agreements')),
  setPage: (payload) => dispatch(setIpmaCisPageCreator('agreements', payload)),
  setPerPage: (payload) => dispatch(setIpmaCisPerPageCreator('agreements', payload)),
  setSort: (payload) => dispatch(setIpmaCisSortCreator('agreements', payload)),
  setFilters: (payload) => dispatch(setIpmaCisFiltersCreator('agreements', payload)),
  removeFilters: () => dispatch(removeIpmaCisFiltersCreator('agreements')),
  setProfile: (payload) => dispatch(setIpmaCisProfileCreator('agreements', payload)),
  removeAgreement: (payload) => dispatch(removeIpmaCisCreator('agreements', payload)),
  loadAgreementsAttributes: () => dispatch(loadGlobalCisAgreementsAttributesCreator()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Agreements);
